import React, { useMemo, useState } from 'react';
import type { EpicSession, StorySpec, StorySummary } from '../../../../../../shared/types/epics';
import { buildRetryClusters } from '../../../../../../shared/lib/retryClusters';
import { scoreStory } from '../../../../../../shared/lib/verification';
import { StateIndicator } from '../../../../components/shared/StateIndicator';
import { Card, CardHeader, CardBody, StatusBadge } from '../../../../components/ui';
import { formatCost, formatDate, storyAnchorId } from '../format';
import { VerificationBadge } from './verification';
import { StoryActionsBar } from './StoryActionsBar';
import { LiveAgentCard } from './LiveAgentCard';
import { FailureAnalysisPanel } from './FailureAnalysisPanel';
import { SafetyNetPanel } from './SafetyNetPanel';
import { StorySpecPanel } from './StorySpecPanel';
import { RetryClusters } from './RetryClusters';

export const StoryCard: React.FC<{
  epicId: number;
  story: StorySummary;
  spec?: StorySpec;
  sessions: EpicSession[];
  onOpenTranscript: (agentType: string, taskArn?: string | null) => void;
  onActionSuccess: () => void;
}> = ({ epicId, story, spec, sessions, onOpenTranscript, onActionSuccess }) => {
  const verification = useMemo(
    () => scoreStory(story, buildRetryClusters(sessions)),
    [story, sessions],
  );
  const liveAgents = (story.liveAgents && story.liveAgents.length > 0)
    ? story.liveAgents
    : story.liveAgent ? [story.liveAgent] : [];
  // A settled story (fully verified, nothing running) is done — collapse its
  // agent-runs table by default so it doesn't eat vertical space. Anything that
  // still needs eyes (in-flight, partial/weak verification, a failure) stays open.
  const settled = verification.band === 'strong' && liveAgents.length === 0;
  const [expanded, setExpanded] = useState(!settled);
  return (
  <div id={storyAnchorId(story.id)} className="scroll-mt-24">
  <Card>
    <CardHeader>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse agent runs' : 'Expand agent runs'}
        data-testid={`story-toggle-${story.id}`}
        className="shrink-0 self-start mt-0.5 text-slate-400 hover:text-slate-600"
      >
        {expanded ? '▾' : '▸'}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-slate-500">
            STORY · {story.id}
          </span>
          <StateIndicator state={story.state} />
          {story.storyPoints !== null && (
            <StatusBadge tone="info">{story.storyPoints} SP</StatusBadge>
          )}
          <VerificationBadge v={verification} />
        </div>
        <h3 className="text-base font-semibold text-slate-900 mt-0.5 truncate">
          {story.title || <span className="italic text-slate-500">(untitled)</span>}
        </h3>
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <div className="text-right text-xs text-slate-600">
          <div className="font-mono tabular-nums text-slate-900 font-semibold">
            {formatCost(story.lifetimeCostUsd)}
          </div>
          <div>
            {story.sessionCount} runs
            {liveAgents.filter((la) => !la.stale).length > 0 &&
              ` · ${liveAgents.filter((la) => !la.stale).length} in progress`}
            {' · updated '}{formatDate(story.updatedAt)}
          </div>
        </div>
        <StoryActionsBar epicId={epicId} story={story} onSuccess={onActionSuccess} />
      </div>
    </CardHeader>
    {liveAgents.map((la) => (
      <LiveAgentCard key={la.agentType} live={la} />
    ))}
    {story.failureAnalysis && <FailureAnalysisPanel analysis={story.failureAnalysis} />}
    {expanded && (
      <>
        <SafetyNetPanel story={story} />
        <StorySpecPanel spec={spec} />
        <CardBody className="p-0">
          <RetryClusters
            sessions={sessions}
            onOpenTranscript={onOpenTranscript}
            hasLiveAgent={liveAgents.some((la) => !la.stale)}
            hasCost={(story.lifetimeCostUsd ?? 0) > 0}
          />
        </CardBody>
      </>
    )}
  </Card>
  </div>
  );
};
